import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, MessageSquare, Loader2, ChevronLeft, RefreshCw, ChevronUp, User, Wifi, WifiOff } from 'lucide-react';
import { wahaChatsApi, WahaChat, WahaMessageItem, WahaConnectionInfo } from '../services/wahaChatsApi';

interface WahaHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string; // The selected agent ID to look up WAHA session
}

const formatTimestamp = (ts: number): string => {
  const date = new Date(ts * 1000);
  return date.toLocaleString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatRelativeDate = (ts: number | null): string => {
  if (!ts) return '';
  const now = Date.now();
  const date = new Date(ts * 1000);
  const diffMs = now - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) return 'Hace unos minutos';
  if (diffHours < 24) return `Hace ${Math.floor(diffHours)}h`;
  if (diffHours < 48) return 'Ayer';
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' });
};

const MESSAGES_PER_PAGE = 10;

const WahaHistoryPanel: React.FC<WahaHistoryPanelProps> = ({ isOpen, onClose, agentId }) => {
  const [connection, setConnection] = useState<WahaConnectionInfo | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Chat list
  const [chats, setChats] = useState<WahaChat[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);

  // Selected chat messages
  const [selectedChat, setSelectedChat] = useState<WahaChat | null>(null);
  const [messages, setMessages] = useState<WahaMessageItem[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Reset state when panel closes or agentId changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedChat(null);
      setMessages([]);
      setMessagesOffset(0);
      setHasMoreMessages(false);
    }
  }, [isOpen]);

  // Check WAHA connection via agent ID
  useEffect(() => {
    if (!isOpen || !agentId) return;

    const checkConnection = async () => {
      setConnectionLoading(true);
      setConnectionError(null);
      setConnection(null);
      setChats([]);
      try {
        const conn = await wahaChatsApi.getSessionForAgent(agentId);
        setConnection(conn);
        if (!conn) {
          setConnectionError('Este agente no tiene una conexion WhatsApp (WAHA) activa');
        }
      } catch {
        setConnectionError('Error al verificar la conexion WAHA');
      } finally {
        setConnectionLoading(false);
      }
    };

    checkConnection();
  }, [isOpen, agentId]);

  // Load chats when connection is available
  useEffect(() => {
    if (!connection) return;

    const loadChats = async () => {
      setChatsLoading(true);
      try {
        const result = await wahaChatsApi.getChats(connection.sessionName, 20);
        // Sort by timestamp descending (most recent first)
        const sorted = result.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setChats(sorted);
      } catch (err: any) {
        console.error('[WahaHistory] Error loading chats:', err);
        setConnectionError('Error al cargar los contactos desde WhatsApp');
      } finally {
        setChatsLoading(false);
      }
    };

    loadChats();
  }, [connection]);

  // Load messages for selected chat
  const loadMessages = useCallback(async (chat: WahaChat, offset = 0) => {
    if (!connection) return;

    if (offset === 0) {
      setMessagesLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const result = await wahaChatsApi.getMessages(
        connection.sessionName,
        chat.id,
        MESSAGES_PER_PAGE,
        offset
      );

      if (offset === 0) {
        // Reverse so oldest is at top, newest at bottom (chat order)
        setMessages([...result.messages].reverse());
      } else {
        // Prepend older messages at the top
        setMessages(prev => [...[...result.messages].reverse(), ...prev]);
      }
      setHasMoreMessages(result.hasMore);
      setMessagesOffset(offset + MESSAGES_PER_PAGE);
    } catch (err: any) {
      console.error('[WahaHistory] Error loading messages:', err);
    } finally {
      setMessagesLoading(false);
      setLoadingMore(false);
    }
  }, [connection]);

  // Auto-scroll to bottom when messages load for the first time
  useEffect(() => {
    if (messages.length > 0 && !loadingMore) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesLoading]);

  const handleSelectChat = (chat: WahaChat) => {
    setSelectedChat(chat);
    setMessages([]);
    setMessagesOffset(0);
    loadMessages(chat, 0);
  };

  const handleLoadMore = () => {
    if (selectedChat && !loadingMore) {
      loadMessages(selectedChat, messagesOffset);
    }
  };

  const handleBack = () => {
    setSelectedChat(null);
    setMessages([]);
    setMessagesOffset(0);
    setHasMoreMessages(false);
  };

  const handleRefreshChats = async () => {
    if (!connection) return;
    setChatsLoading(true);
    try {
      const result = await wahaChatsApi.getChats(connection.sessionName, 20);
      const sorted = result.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setChats(sorted);
    } catch {
      // silently fail
    } finally {
      setChatsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      {/* Backdrop */}
      <div className="bg-black/30 absolute inset-0" onClick={onClose} />

      {/* Panel — wider for better readability */}
      <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
          <div className="flex items-center gap-3">
            {selectedChat && (
              <button onClick={handleBack} className="p-1 hover:bg-gray-100 rounded-full transition">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            <div>
              <h2 className="font-semibold text-gray-900 text-sm">
                {selectedChat ? selectedChat.name : 'Historial WhatsApp'}
              </h2>
              <p className="text-xs text-gray-500">
                {selectedChat
                  ? `+${selectedChat.phone}`
                  : connection
                    ? `Sesion: ${connection.sessionName}`
                    : 'Verificando conexion...'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!selectedChat && connection && (
              <button
                onClick={handleRefreshChats}
                disabled={chatsLoading}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <RefreshCw className={`w-4 h-4 text-gray-500 ${chatsLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Loading connection check */}
          {connectionLoading && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <p className="text-sm text-gray-500">Verificando conexion WAHA...</p>
            </div>
          )}

          {/* No connection */}
          {!connectionLoading && connectionError && (
            <div className="flex flex-col items-center justify-center h-64 gap-3 px-6">
              <WifiOff className="w-12 h-12 text-gray-300" />
              <p className="text-sm text-gray-500 text-center">{connectionError}</p>
              <p className="text-xs text-gray-400 text-center">
                Los mensajes se muestran desde la base de datos local.
              </p>
            </div>
          )}

          {/* Chat list — sorted by most recent */}
          {!connectionLoading && !connectionError && !selectedChat && (
            <>
              {chatsLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : chats.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2">
                  <MessageSquare className="w-8 h-8 text-gray-300" />
                  <p className="text-sm text-gray-500">No hay conversaciones</p>
                </div>
              ) : (
                <div className="divide-y">
                  {chats.map(chat => (
                    <button
                      key={chat.id}
                      onClick={() => handleSelectChat(chat)}
                      className="w-full px-5 py-3.5 flex items-center gap-3 hover:bg-gray-50 transition text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 truncate">{chat.name}</p>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatRelativeDate(chat.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          +{chat.phone}
                          {chat.lastMessage && ` · ${chat.lastMessage.substring(0, 50)}`}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Connection indicator */}
              {connection && (
                <div className="px-5 py-3 border-t bg-gray-50 sticky bottom-0">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Wifi className="w-3 h-3 text-emerald-500" />
                    <span>Conectado via WAHA · {chats.length} contactos recientes</span>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Messages view */}
          {!connectionLoading && !connectionError && selectedChat && (
            <div className="flex flex-col h-full">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2">
                  <MessageSquare className="w-8 h-8 text-gray-300" />
                  <p className="text-sm text-gray-500">Sin mensajes</p>
                </div>
              ) : (
                <>
                  {/* Load older messages button — at top */}
                  {hasMoreMessages && (
                    <div className="px-5 py-3 border-b sticky top-0 bg-white z-10">
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition"
                      >
                        {loadingMore ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <ChevronUp className="w-3.5 h-3.5" />
                        )}
                        Cargar mensajes anteriores
                      </button>
                    </div>
                  )}

                  {/* Messages — oldest first (top), newest at bottom */}
                  <div className="px-4 py-3 space-y-2.5 flex-1">
                    {messages.map((msg, idx) => (
                      <div
                        key={msg.id || idx}
                        className={`flex flex-col ${msg.fromMe ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                            msg.fromMe
                              ? 'bg-emerald-100 text-gray-900 rounded-br-md'
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}
                        >
                          {msg.hasMedia && msg.mediaType && (
                            <div className="text-xs text-gray-500 mb-1 italic flex items-center gap-1">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                              {msg.mediaType === 'audio' ? 'Audio' :
                                msg.mediaType === 'image' ? 'Imagen' :
                                msg.mediaType === 'video' ? 'Video' :
                                msg.mediaType === 'document' ? 'Documento' :
                                msg.mediaType === 'sticker' ? 'Sticker' : 'Media'}
                            </div>
                          )}
                          {msg.body && (
                            <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                          )}
                          {!msg.body && msg.hasMedia && (
                            <p className="italic text-gray-400 text-xs">Contenido multimedia</p>
                          )}
                        </div>
                        <span className={`text-[10px] text-gray-400 mt-0.5 ${msg.fromMe ? 'mr-1' : 'ml-1'}`}>
                          {formatTimestamp(msg.timestamp)}
                        </span>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WahaHistoryPanel;
